<!-- 
============================================================
  EXAMINATION MANAGEMENT
  Tabs: Exams · Schedule · Mark Entry · Results · Reports
============================================================
 -->
<div class="exam-page">

  <!-- Toast -->
  @if (saveSuccess()) {
    <div class="exam-toast alert alert-success d-flex align-items-center gap-2 shadow">
      <i class="bi bi-check-circle-fill fs-5"></i><strong>{{ saveSuccess() }}</strong>
    </div>
  }

  <!-- Delete Modal -->
  @if (showDeleteModal()) {
    <div class="exam-overlay" (click)="showDeleteModal.set(false)">
      <div class="exam-modal exam-modal-sm" (click)="$event.stopPropagation()">
        <div class="emh del-hdr"><div class="emh-icon"><i class="bi bi-trash3-fill"></i></div>
          <div><h5 class="emh-title">Delete?</h5><p class="emh-sub">{{ deleteCtx()?.label }}</p></div>
          <button class="emh-close" (click)="showDeleteModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="emb p-4 text-center"><p class="text-muted small mb-0">This cannot be undone.</p></div>
        <div class="emf"><button class="btn btn-outline-secondary px-4" (click)="showDeleteModal.set(false)">Cancel</button>
          <button class="btn btn-danger px-4" (click)="executeDelete()"><i class="bi bi-trash3 me-2"></i>Delete</button></div>
      </div>
    </div>
  }

  <!-- Hall Ticket Modal -->
  @if (showHallTicket() && hallTicketData()) {
    @let ht = hallTicketData()!;
    @let htExam = exams().find(e => e.id === ht.exam_id)!;
    <div class="exam-overlay" (click)="showHallTicket.set(false)">
      <div class="hall-ticket-modal" (click)="$event.stopPropagation()">
        <div class="ht-header">
          <div class="ht-school-logo"><i class="bi bi-mortarboard-fill"></i></div>
          <div>
            <!-- <div class="ht-school-name">EduSmart International School</div> -->
            <div class="ht-title">HALL TICKET / ADMIT CARD</div>
            <div class="ht-exam-name">{{ htExam?.exam_name }}</div>
          </div>
          <button class="ht-close no-print" (click)="showHallTicket.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="ht-body">
          <div class="ht-student-row">
            <div class="ht-av">{{ ht.student_name.charAt(0) }}</div>
            <div class="ht-student-info">
              <div class="ht-student-name">{{ ht.student_name }}</div>
              <div class="ht-student-meta">{{ ht.class_name }} · Roll No: {{ ht.roll_no }} · Reg: {{ ht.student_id }}</div>
            </div>
            <div class="ht-ay">AY: {{ htExam?.academic_year }}</div>
          </div>
          <table class="ht-schedule-table">
            <thead><tr><th>Date</th><th>Day</th><th>Subject</th><th>Time</th><th>Duration</th><th>Room</th><th>Max Marks</th></tr></thead>
            <tbody>
              @for (s of htExam?.schedule ?? []; track s.subject) {
                <tr>
                  <td class="fw-semibold">{{ formatDate(s.date) }}</td>
                  <td class="text-muted small">{{ getDayName(s.date) }}</td>
                  <td class="fw-semibold">{{ s.subject }}</td>
                  <td>{{ formatTime(s.start_time) }}</td>
                  <td>{{ s.duration }} min</td>
                  <td>{{ s.room_no || '—' }}</td>
                  <td class="text-center">{{ s.max_marks }}</td>
                </tr>
              }
            </tbody>
          </table>
          <div class="ht-instructions">
            <p class="fw-semibold small mb-2">Instructions:</p>
            <ul class="small text-muted mb-0" style="padding-left:18px;">
              <li>Carry this hall ticket to every examination session.</li>
              <li>Report at least 15 minutes before the examination starts.</li>
              <li>Electronic devices (mobile phones, calculators – unless allowed) are strictly prohibited.</li>
              <li>Use blue/black ballpoint pen. Pencil may be used for diagrams only.</li>
            </ul>
          </div>
          <div class="ht-signature-row">
            <div class="ht-sig"><div class="ht-sig-line"></div><div class="ht-sig-label">Student Signature</div></div>
            <div class="ht-sig"><div class="ht-sig-line"></div><div class="ht-sig-label">Parent / Guardian</div></div>
            <div class="ht-sig"><div class="ht-sig-line"></div><div class="ht-sig-label">Principal</div></div>
          </div>
        </div>
        <div class="ht-footer no-print">
          <button class="btn btn-outline-secondary px-4" (click)="showHallTicket.set(false)">Close</button>
          <button class="btn exam-btn-primary px-4" (click)="printHallTicket()"><i class="bi bi-printer-fill me-2"></i>Print Hall Ticket</button>
        </div>
      </div>
    </div>
  }

  <!-- Student Marksheet / Report Card -->
  @if (marksheetStudent()) {
    @let ms = marksheetStudent()!;
    @let msExam = getMarksheetExam(ms);
    <div class="exam-overlay marksheet-overlay" (click)="closeMarksheet()">
      <div class="marksheet-modal" (click)="$event.stopPropagation()">
        <div class="ms-toolbar no-print">
          <span class="small text-muted">Student Report Card</span>
          <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary btn-sm" (click)="closeMarksheet()">Close</button>
            <button class="btn exam-btn-primary btn-sm" (click)="printMarksheet()"><i class="bi bi-printer-fill me-1"></i>Print</button>
          </div>
        </div>
        <div class="marksheet-sheet">
          <div class="ms-letterhead">
            <div class="ms-logo"><i class="bi bi-mortarboard-fill"></i></div>
            <div class="ms-school">
              <!-- <div class="ms-school-name">EduSmart International School</div>
              <div class="ms-school-addr">Affiliated to CBSE · New Delhi · Affiliation No. 2730123</div> -->
              <div class="ms-school-addr">REPORT CARD / MARKSHEET</div>
              <div class="ms-doc-title">{{ ms.student_name }} - {{dataService.getClassName(ms.class_name)}} Class</div>
            </div>
            <div class="ms-ay-chip">AY {{ msExam?.academic_year }}</div>
          </div>
          <div class="ms-exam-banner">{{ msExam?.exam_name }} · {{ msExam?.exam_type }}</div>
          <div class="ms-student-grid">
            <div class="ms-photo">{{ ms.student_name.charAt(0) }}</div>
            <div class="ms-fields">
              <div class="ms-field"><span>Student Name</span><strong>{{ ms.student_name }}</strong></div>
              <div class="ms-field"><span>Registration No.</span><strong>{{ ms.student_id }}</strong></div>
              <div class="ms-field"><span>Class / Program</span><strong>{{ dataService.getClassName(ms.class_name) }} Class</strong></div>
              <div class="ms-field"><span>Roll No.</span><strong>{{ ms.roll_no }}</strong></div>
              <div class="ms-field"><span>Exam Period</span><strong>{{ formatDate(msExam?.start_date ?? '') }} – {{ formatDate(msExam?.end_date ?? '') }}</strong></div>
              <div class="ms-field"><span>Rank</span><strong>{{ ms.rank || '—' }}</strong></div>
            </div>
          </div>
          <table class="ms-marks-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Subject</th>
                <th class="text-center">Max Marks</th>
                <th class="text-center">Pass Marks</th>
                <th class="text-center">Marks Obtained</th>
                <th class="text-center">Grade</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              @for (sm of ms.subject_marks; track sm.subject; let i = $index) {
                <tr>
                  <td class="text-muted">{{ i + 1 }}</td>
                  <td class="fw-semibold">{{ dataService.getSubjectName(sm.subject) }}</td>
                  <td class="text-center">{{ sm.max_marks }}</td>
                  <td class="text-center text-muted">{{ sm.pass_marks }}</td>
                  <td class="text-center">
                    @if (sm.is_absent) { <span class="absent-chip">AB</span> }
                    @else {
                      <span class="fw-bold" [class.fail-marks]="(sm.marks_obtained ?? 0) < sm.pass_marks">{{ sm.marks_obtained ?? '—' }}</span>
                    }
                  </td>
                  <td class="text-center">
                    <span class="grade-badge" [style.background]="getGradeColor(sm.grade)+'22'" [style.color]="getGradeColor(sm.grade)">{{ sm.grade }}</span>
                  </td>
                  <td class="small text-muted">{{ sm.remarks || (sm.is_absent ? 'Absent' : ((sm.marks_obtained ?? 0) < sm.pass_marks ? 'Failed' : '—')) }}</td>
                </tr>
              }
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2" class="fw-bold">TOTAL</td>
                <td class="text-center fw-bold">{{ ms.max_total }}</td>
                <td></td>
                <td class="text-center fw-bold">{{ ms.total_marks }}</td>
                <td class="text-center">
                  <span class="grade-badge" [style.background]="getGradeColor(ms.grade)+'22'" [style.color]="getGradeColor(ms.grade)">{{ ms.grade }}</span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          <div class="ms-summary">
            <div class="ms-sum-item"><span>Percentage</span><strong [style.color]="getGradeColor(ms.grade)">{{ ms.percentage }}%</strong></div>
            <div class="ms-sum-item"><span>Grade</span><strong [style.color]="getGradeColor(ms.grade)">{{ ms.grade }} · {{ getGradeLabel(ms.grade) }}</strong></div>
            <div class="ms-sum-item"><span>GPA</span><strong>{{ calcGPA(ms.percentage) }} / 10</strong></div>
            <div class="ms-sum-item"><span>Class Rank</span><strong>{{ ms.rank }}</strong></div>
            <div class="ms-sum-item"><span>Result</span><span class="{{ getResultClass(ms.result) }}">{{ ms.result }}</span></div>
          </div>
          <div class="ms-scale">
            <div class="ms-scale-title">Grading Scale</div>
            <div class="ms-scale-row">
              @for (g of gradeRules; track g.grade) {
                <div class="ms-scale-chip">
                  <span [style.color]="g.color" class="fw-bold">{{ g.grade }}</span>
                  <span>{{ g.min }}–{{ g.max }}%</span>
                </div>
              }
            </div>
          </div>
          @if (ms.remarks) {
            <div class="ms-remarks"><strong>Teacher Remarks:</strong> {{ ms.remarks }}</div>
          } @else {
            <div class="ms-remarks"><strong>Teacher Remarks:</strong>
              @if (ms.result === 'Pass' && ms.percentage >= 90) { Outstanding performance. Keep it up. }
              @else if (ms.result === 'Pass' && ms.percentage >= 70) { Very good work. Continue to strive for excellence. }
              @else if (ms.result === 'Pass') { Satisfactory. Focus on weaker subjects for further improvement. }
              @else { Needs significant improvement. Extra coaching and parent support recommended. }
            </div>
          }
          <div class="ms-sign-row">
            <div class="ms-sig"><div class="ms-sig-line"></div><div>Class Teacher</div></div>
            <div class="ms-sig"><div class="ms-sig-line"></div><div>Controller of Examinations</div></div>
            <div class="ms-sig"><div class="ms-sig-line"></div><div>Principal</div></div>
          </div>
          <div class="ms-footnote">This is a computer-generated marksheet. Valid with the school seal.</div>
        </div>
      </div>
    </div>
  }

  <!-- Publish Confirm Modal -->
  @if (publishConfirm()) {
    <div class="exam-overlay" (click)="publishConfirm.set(false)">
      <div class="exam-modal exam-modal-sm" (click)="$event.stopPropagation()">
        <div class="emh pub-hdr"><div class="emh-icon"><i class="bi bi-megaphone-fill"></i></div>
          <div><h5 class="emh-title">Publish Results?</h5><p class="emh-sub">{{ resultExam()?.exam_name }}</p></div>
          <button class="emh-close" (click)="publishConfirm.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="emb p-4 text-center"><p class="text-muted small">Once published, results will be visible to students and parents. This action will also update the exam status to <strong>Results Declared</strong>.</p></div>
        <div class="emf"><button class="btn btn-outline-secondary px-4" (click)="publishConfirm.set(false)">Cancel</button>
          <button class="btn btn-success px-4" (click)="publishResults()"><i class="bi bi-megaphone me-2"></i>Publish Now</button></div>
      </div>
    </div>
  }

  <!-- Add Schedule Subject Modal -->
  @if (showScheduleForm()) {
    <div class="exam-overlay" (click)="showScheduleForm.set(false)">
      <div class="exam-modal exam-modal-md" (click)="$event.stopPropagation()">
        <div class="emh"><div class="emh-icon"><i class="bi bi-calendar-plus-fill"></i></div>
          <div><h5 class="emh-title">Add Subject to Schedule</h5><p class="emh-sub">{{ selectedExam()?.exam_name }}</p></div>
          <button class="emh-close" (click)="showScheduleForm.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="emb p-4">
          <div class="row g-3">
            <div class="col-md-6"><label class="form-label fw-semibold">Subject <span class="req">*</span></label>
              <!-- <input class="form-control" [(ngModel)]="scheduleForm.subject" placeholder="e.g. Physics" [class.is-invalid]="scheduleSubmitted && !scheduleForm.subject"> -->
              <select class="form-select form-control form-control-sm"  [class.is-invalid]="scheduleSubmitted && !scheduleForm.subject"
                [(ngModel)]="scheduleForm.subject">
                <option [ngValue]="''">-subject-</option>
                @for (sub of defaultSubjects; track sub) { <option [ngValue]="sub.code">{{ sub.name }}</option> }
              </select>
            </div>
            <div class="col-md-6"><label class="form-label fw-semibold">Date <span class="req">*</span></label>
              <input type="date" class="form-control" [(ngModel)]="scheduleForm.date" [class.is-invalid]="scheduleSubmitted && !scheduleForm.date"></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Start Time</label>
              <input type="time" class="form-control" [(ngModel)]="scheduleForm.start_time"></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Duration (min)</label>
              <input type="number" class="form-control" [(ngModel)]="scheduleForm.duration" min="30"></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Room</label>
              <!-- <input class="form-control" [(ngModel)]="scheduleForm.room_no" placeholder="Room 101"> -->
              <select class="form-select form-control form-control-sm" [(ngModel)]="scheduleForm.room_no">
                <option value="">-- Select Room --</option>
                @for (r of roomOptions; track r) { <option [value]="r.code">{{ r.name }}</option> }
              </select>
            </div>
            <div class="col-md-6"><label class="form-label fw-semibold">Max Marks</label>
              <input type="number" class="form-control" [(ngModel)]="scheduleForm.max_marks" min="1"></div>
            <div class="col-md-6"><label class="form-label fw-semibold">Pass Marks</label>
              <input type="number" class="form-control" [(ngModel)]="scheduleForm.pass_marks" min="0"></div>
          </div>
        </div>
        <div class="emf">
          <button class="btn btn-outline-secondary px-4" (click)="showScheduleForm.set(false)">Cancel</button>
          <button class="btn exam-btn-primary px-4" (click)="saveSchedule()"><i class="bi bi-check-circle-fill me-2"></i>Add Subject</button>
        </div>
      </div>
    </div>
  }

  <!-- Marks Entry Modal -->
  @if (editingMarks()) {
    @let mr = editingMarks()!;
    <div class="exam-overlay" (click)="closeMarksModal()">
      <div class="marks-modal" (click)="$event.stopPropagation()">
        <div class="emh"><div class="emh-icon"><i class="bi bi-pencil-fill"></i></div>
          <div>
            <h5 class="emh-title">{{ isNewMarks() ? 'Add Student Marks' : 'Enter Marks' }}</h5>
            <p class="emh-sub">{{ isNewMarks() ? (selectedExam()?.exam_name) : (mr.student_name + ' · ' + mr.roll_no) }}</p>
          </div>
          <button class="emh-close" (click)="closeMarksModal()"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="emb p-4">
          @if (isNewMarks()) {
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label fw-semibold">Class</label>
                <!-- <input class="form-control" [(ngModel)]="mr.class_name" readonly> -->
                <select class="form-select form-control" [(ngModel)]="mr.class_name"  
                  style="pointer-events: none; background-color: #f0f0f0;" tabindex="-1">
                  <option value="">All Classes</option>
                  @for (c of classOptions; track c) { <option [ngValue]="c.code">{{ c.name }}</option> }
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Roll No. <span class="req">*</span></label>
                <!-- <input class="form-control" [(ngModel)]="mr.roll_no" placeholder="e.g. 515580" 
                  [class.is-invalid]="marksSubmitted && !mr.roll_no"
                  (keyup.enter)="onSearchStd(mr)" (keydown.Tab)="onSearchStd(mr)"> -->
                <select class="form-select form-control" [(ngModel)]="mr.roll_no"
                  (change)="onSearchStd(mr)">
                  <option value="">--</option>
                  @for (s of students4marks; track s) { <option [ngValue]="s.roll_no">{{ s.name }} - (R.No.: {{s.roll_no}})</option> }
                </select>                
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Student Name <span class="req">*</span></label>
                <input class="form-control" [(ngModel)]="mr.student_name" placeholder="Full name" [class.is-invalid]="marksSubmitted && !mr.student_name">
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Registration No.</label>
                <input class="form-control" [(ngModel)]="mr.student_id" placeholder="e.g. S020">
              </div>
            </div>
          }
          <table class="marks-entry-table">
            <thead><tr><th>Subject</th><th class="text-center">Max</th><th class="text-center">Pass</th><th class="text-center">Absent</th><th style="width:100px">Marks</th></tr></thead>
            <tbody>
              @for (sm of mr.subject_marks; track sm.subject; let i = $index) {
                <tr>
                  <td class="fw-semibold small">{{ dataService.getSubjectName(sm.subject) }}</td>
                  <td class="text-center text-muted small">{{ sm.max_marks }}</td>
                  <td class="text-center text-muted small">{{ sm.pass_marks }}</td>
                  <td class="text-center">
                    <div class="form-check mb-0 d-flex justify-content-center">
                      <input type="checkbox" class="form-check-input" [(ngModel)]="sm.is_absent" [name]="'absent'+i" (change)="sm.is_absent ? sm.marks_obtained = null : null">
                    </div>
                  </td>
                  <td>
                    <input type="number" class="form-control form-control-sm text-center"
                      [(ngModel)]="sm.marks_obtained"
                      [name]="'marks'+i"
                      [disabled]="sm.is_absent"
                      min="0" [max]="sm.max_marks"
                      [class.is-invalid]="!sm.is_absent && sm.marks_obtained !== null && (sm.marks_obtained! < 0 || sm.marks_obtained! > sm.max_marks)">
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <div class="emf">
          <button class="btn btn-outline-secondary px-4" (click)="closeMarksModal()">Cancel</button>
          <button class="btn exam-btn-primary px-5" (click)="saveMarks()"><i class="bi bi-check-circle-fill me-2"></i>{{ isNewMarks() ? 'Add Marks' : 'Save Marks' }}</button>
        </div>
      </div>
    </div>
  }

  <!-- ===================== PAGE HEADER ===================== -->
  <div class="exam-ph mb-4">
    <div>
      <h1 class="exam-title"><i class="bi bi-journal-bookmark-fill me-2 text-primary" style="font-size:1.1rem;"></i>Examination Management</h1>
      <p class="exam-subtitle">Exams · Schedule · Mark Entry · Results · Reports</p>
    </div>
    <div class="d-flex gap-2 flex-wrap">
      @if (activeTab() === 'exams' && examMode() === 'list') {
        <button class="btn exam-btn-primary btn-sm" (click)="openNewExam()"><i class="bi bi-plus-circle-fill me-1"></i>New Exam</button>
      }
      @if (activeTab() === 'schedule') {
        <button class="btn exam-btn-primary btn-sm" (click)="openNewSchedule()" [disabled]="!selectedExam()"><i class="bi bi-plus-circle-fill me-1"></i>Add Subject</button>
      }
      @if (activeTab() === 'marks') {
        <button class="btn exam-btn-primary btn-sm" (click)="openNewMarkEntry()" [disabled]="!selectedExam()"><i class="bi bi-plus-circle-fill me-1"></i>Add Student Marks</button>
      }
      @if (activeTab() === 'exams' && examMode() !== 'list') {
        <button class="btn btn-outline-secondary btn-sm" (click)="examMode.set('list')"><i class="bi bi-arrow-left me-1"></i>Back</button>
      }
    </div>
  </div>

  <!-- Stats Row -->
  <!-- <div class="exam-stats-row mb-4">
    <div class="esr-item">
      <div class="esr-icon" style="background:linear-gradient(135deg,#1a3a6b,#2d6a9f)">
        <i class="bi bi-journal-check"></i>
      </div>
      <div class="d-flex flex-column">
        <div class="esr-val">{{ examStats().total }}</div>
        <div class="esr-label">Total Exams</div>
      </div>
    </div>
    <div class="esr-item">
      <div class="esr-icon" style="background:linear-gradient(135deg,#0284c7,#38bdf8)">
        <i class="bi bi-hourglass-split"></i>
      </div>
      <div class="d-flex flex-column">
        <div class="esr-val text-info">{{ examStats().upcoming }}</div>
        <div class="esr-label">Upcoming</div>
      </div>
    </div>
    <div class="esr-item">
      <div class="esr-icon" style="background:linear-gradient(135deg,#d97706,#f59e0b)">
        <i class="bi bi-pencil-fill"></i>
      </div>
      <div class="d-flex flex-column">
        <div class="esr-val text-warning">{{ examStats().ongoing }}</div>
        <div class="esr-label">Ongoing</div>
      </div>
    </div>
    <div class="esr-item">
      <div class="esr-icon" style="background:linear-gradient(135deg,#059669,#10b981)">
        <i class="bi bi-check-circle-fill"></i>
      </div>
      <div class="d-flex flex-column">
        <div class="esr-val text-success">{{ examStats().completed }}</div>
        <div class="esr-label">Completed</div>
      </div>
    </div>
    <div class="esr-item">
      <div class="esr-icon" style="background:linear-gradient(135deg,#7c3aed,#a78bfa)">
        <i class="bi bi-people-fill"></i>
      </div>
      <div class="d-flex flex-column">
        <div class="esr-val text-purple">{{ results().length }}</div>
        <div class="esr-label">Results Entered</div>
      </div>
    </div>
  </div> -->

  <!-- TABS -->
  <div class="exam-tabs mb-4">
    @for (t of [{k:'exams',l:'Exams',ic:'bi-journal-check'},{k:'schedule',l:'Schedule',ic:'bi-calendar-week-fill'},{k:'marks',l:'Mark Entry',ic:'bi-pencil-fill'},{k:'results',l:'Results',ic:'bi-award-fill'},{k:'reports',l:'Reports',ic:'bi-bar-chart-fill'}]; track t.k) {
      <button class="etab" [class.active]="activeTab() === t.k"
        (click)="activeTab.set($any(t.k)); examMode.set('list')">
        <i class="bi {{ t.ic }} me-1"></i>{{ t.l }}
      </button>
    }
  </div>

  <!-- ===================== EXAMS TAB ===================== -->
  @if (activeTab() === 'exams') {

    @if (examMode() === 'list') {
      <!-- Filters -->
      <div class="exam-card mb-3"><div class="ec-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-4"><label class="form-label small fw-semibold text-muted">Search</label>
            <div class="input-group"><span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
              <input class="form-control border-start-0" placeholder="Exam name or class…" [ngModel]="examSearch()" (ngModelChange)="examSearch.set($event)">
              @if (examSearch()) { <button class="btn btn-outline-secondary" (click)="examSearch.set('')"><i class="bi bi-x"></i></button> }
            </div></div>
          <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Type</label>
            <select class="form-select" [ngModel]="examTypeFilter()" (ngModelChange)="examTypeFilter.set($event)">
              <option [ngValue]="''">All Types</option>
              @for (t of examTypes; track t) { <option [ngValue]="t">{{ t }}</option> }
            </select></div>
          <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Class</label>
            <select class="form-select" [ngModel]="examClassFilter()" (ngModelChange)="examClassFilter.set($event)">
              <option [ngValue]="''">All Classes</option>
              @for (c of classOptions; track c) { <option [ngValue]="c.code">{{ c.name }}</option> }
            </select></div>
          <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Status</label>
            <select class="form-select" [ngModel]="examStatusFilter()" (ngModelChange)="examStatusFilter.set($event)">
              <option [ngValue]="''">All</option>
              @for (s of examStatuses; track s) { <option [ngValue]="s">{{ s }}</option> }
            </select></div>
          <div class="col-auto"><button class="btn btn-outline-secondary" (click)="resetFilters()"><i class="bi bi-arrow-counterclockwise me-1"></i>Reset</button></div>
        </div>
      </div></div>

      @if (filteredExams().length === 0) {
        <div class="empty-state"><i class="bi bi-journal-x"></i><p>No exams found</p></div>
      } @else {
        <div class="exams-grid">
          @for (e of filteredExams(); track e.id) {
            <div class="exam-card-item" [style.--exam-color]="getExamTypeColor(e.exam_type)">
              <div class="eci-top">
                <div class="eci-type-badge" [style.background]="getExamTypeColor(e.exam_type) + '22'" [style.color]="getExamTypeColor(e.exam_type)">{{ e.exam_type }}</div>
                <span class="{{ getExamStatusClass(e.status) }}">{{ e.status }}</span>
              </div>
              <h5 class="eci-name">{{ e.exam_name }}</h5>
              <div class="eci-class"><i class="bi bi-people-fill me-1 text-primary"></i>{{ dataService.getClassName(e.class_name) }} Class</div>
              <div class="eci-dates">
                <div class="ecd-item"><i class="bi bi-calendar-event me-1"></i>{{ formatDate(e.start_date) }}</div>
                <div class="ecd-sep">→</div>
                <div class="ecd-item">{{ formatDate(e.end_date) }}</div>
              </div>
              <div class="eci-meta">
                <div><i class="bi bi-book me-1"></i>{{ e.schedule.length }} subjects</div>
                <div><i class="bi bi-mortarboard me-1"></i>AY {{ e.academic_year }}</div>
              </div>
              @if (e.description) { <p class="eci-desc">{{ e.description }}</p> }
              <div class="eci-footer">
                <button class="ecf-btn view"   (click)="viewExam(e)"><i class="bi bi-eye-fill me-1"></i>View</button>
                <button class="ecf-btn edit"   (click)="editExam(e)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
                <button class="ecf-btn status" (click)="toggleExamStatus(e)" title="Advance status"><i class="bi bi-arrow-right-circle-fill"></i></button>
                <button class="ecf-btn delete" (click)="confirmDelete('exam',e.id,e.exam_name)"><i class="bi bi-trash3-fill"></i></button>
              </div>
            </div>
          }
        </div>
      }
    }

    <!-- EXAM FORM -->
    @if (examMode() === 'form') {
      <div class="exam-card">
        <div class="ec-header"><i class="bi bi-journal-plus me-2"></i>{{ editingExamId() !== null ? 'Edit Exam' : 'Create New Exam' }}</div>
        <div class="ec-body">
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <label class="form-label fw-semibold">Exam Name <span class="req">*</span></label>
              <input class="form-control" [(ngModel)]="examForm.exam_name" placeholder="e.g. Mid-Term Examination 2025-26" [class.is-invalid]="examSubmitted && !examForm.exam_name">
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Exam Type</label>
              <select class="form-select" [(ngModel)]="examForm.exam_type">@for(t of examTypes;track t){<option [value]="t">{{ t }}</option>}</select>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Academic Year</label>
              <select class="form-select" [(ngModel)]="examForm.academic_year">@for(y of academicYears;track y){<option [value]="y">{{ y }}</option>}</select>
            </div>

            <div class="col-md-3">
              <label class="form-label fw-semibold">Class / Program <span class="req">*</span></label>
              <select class="form-select" [(ngModel)]="examForm.class_name" (change)="onClassChange()" [class.is-invalid]="examSubmitted && !examForm.class_name">
                <option value="">-- Select --</option>@for(c of classOptions;track c){<option [value]="c.code">{{ c.name }}</option>}
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Status</label>
              <select class="form-select" [(ngModel)]="examForm.status">@for(s of examStatuses;track s){<option [value]="s">{{ s }}</option>}</select>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Start Date <span class="req">*</span></label>
              <input type="date" class="form-control" [(ngModel)]="examForm.start_date" [class.is-invalid]="examSubmitted && !examForm.start_date">
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">End Date</label>
              <input type="date" class="form-control" [(ngModel)]="examForm.end_date">
            </div>

            <div class="col-12">
              <label class="form-label fw-semibold">Description</label>
              <textarea class="form-control" [(ngModel)]="examForm.description" rows="3" maxlength="300" placeholder="Brief description of scope and coverage…"></textarea>
            </div>
          </div>

          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="sub-label mb-0">Subject Schedule</h6>
            <button type="button" class="btn btn-add-row" (click)="addScheduleRow()"><i class="bi bi-plus-circle me-1"></i>Add Subject</button>
          </div>
          <div class="table-responsive mb-4">
            <table class="schedule-form-table">
              <thead><tr><th>Subject</th><th>Date</th><th>Start Time</th><th>Duration (min)</th><th>Max Marks</th><th>Pass Marks</th><th>Room</th><th></th></tr></thead>
              <tbody>
                @for (row of examForm.schedule ?? []; track $index; let i = $index) {
                  <tr>
                    <td>
                      <!-- <input class="form-control form-control-sm" [(ngModel)]="row.subject" [name]="'sub'+i" placeholder="Subject name"> -->
                      <select class="form-select form-control form-control-sm" [(ngModel)]="row.subject" [name]="'sub'+i">
                        <option [ngValue]="''">-subject-</option>
                        @for (sub of defaultSubjects; track sub) { <option [ngValue]="sub.code">{{ sub.name }}</option> }
                      </select>
                    </td>
                    <td><input type="date" class="form-control form-control-sm" [(ngModel)]="row.date" [name]="'date'+i"></td>
                    <td><input type="time" class="form-control form-control-sm" [(ngModel)]="row.start_time" [name]="'time'+i"></td>
                    <td><input type="number" class="form-control form-control-sm" [(ngModel)]="row.duration" [name]="'dur'+i" min="30"></td>
                    <td><input type="number" class="form-control form-control-sm" [(ngModel)]="row.max_marks" [name]="'max'+i" min="1"></td>
                    <td><input type="number" class="form-control form-control-sm" [(ngModel)]="row.pass_marks" [name]="'pass'+i" min="0"></td>
                    <td>
                      <!-- <input class="form-control form-control-sm" [(ngModel)]="row.room_no" [name]="'room'+i" placeholder="Room 101"> -->
                      <select class="form-select form-control form-control-sm" [(ngModel)]="row.room_no" [name]="'room'+i">
                        <option value="">-- Select Room --</option>
                        @for (r of roomOptions; track r) { <option [value]="r.code">{{ r.name }}</option> }
                      </select>
                    </td>
                    <td><button type="button" class="btn-remove-row" (click)="removeScheduleRow(i)" [disabled]="(examForm.schedule?.length ?? 0) === 1"><i class="bi bi-x-lg"></i></button></td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-end gap-3">
            <button class="btn btn-outline-secondary px-4" (click)="examMode.set('list')">Cancel</button>
            <button class="btn exam-btn-primary px-5" (click)="saveExam()">
              <i class="bi bi-check-circle-fill me-2"></i>
              {{ editingExamId() !== null ? 'Update' : 'Save' }} Exam
            </button>
          </div>
        </div>
      </div>
    }

    <!-- EXAM DETAIL -->
    @if (examMode() === 'detail' && getViewExam()) {
      @let ev = getViewExam()!;
      <div class="row g-4">
        <div class="col-lg-4">
          <div class="exam-card">
            <div class="ec-detail-header" [style.background]="'linear-gradient(135deg,' + getExamTypeColor(ev.exam_type) + ',' + getExamTypeColor(ev.exam_type) + '88)'">
              <div class="edh-type">{{ ev.exam_type }}</div>
              <h5 class="edh-name">{{ ev.exam_name }}</h5>
              <span class="{{ getExamStatusClass(ev.status) }}">{{ ev.status }}</span>
            </div>
            <div class="ec-body">
              <div class="detail-list">
                <div class="dl-row"><span>Class</span><span class="fw-semibold">{{ ev.class_name }}</span></div>
                <div class="dl-row"><span>Academic Year</span><span>{{ ev.academic_year }}</span></div>
                <div class="dl-row"><span>Start Date</span><span>{{ formatDate(ev.start_date) }}</span></div>
                <div class="dl-row"><span>End Date</span><span>{{ formatDate(ev.end_date) }}</span></div>
                <div class="dl-row"><span>Subjects</span><span class="fw-bold text-primary">{{ ev.schedule.length }}</span></div>
              </div>
              @if (ev.description) { <p class="small text-muted mt-3 mb-3">{{ ev.description }}</p> }
              <div class="d-flex gap-2 mt-2">
                <button class="btn exam-btn-primary btn-sm w-100" (click)="editExam(ev)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
                <button class="btn btn-outline-danger btn-sm" (click)="confirmDelete('exam',ev.id,ev.exam_name)"><i class="bi bi-trash3"></i></button>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-8">
          <div class="exam-card">
            <div class="ec-header"><i class="bi bi-calendar-week-fill me-2"></i>Exam Schedule</div>
            <div class="ec-body p-0">
              <table class="exam-table">
                <thead><tr><th>#</th><th>Subject</th><th>Date</th><th>Day</th><th>Time</th><th>Duration</th><th>Room</th><th class="text-center">Max</th><th class="text-center">Pass</th></tr></thead>
                <tbody>
                  @for (s of ev.schedule; track s.subject; let i = $index) {
                    <tr>
                      <td class="text-muted small">{{ i+1 }}</td>
                      <td class="fw-semibold small">{{ s.subject }}</td>
                      <td class="small">{{ formatDate(s.date) }}</td>
                      <td class="small text-muted">{{ getDayName(s.date) }}</td>
                      <td class="small">{{ formatTime(s.start_time) }}</td>
                      <td class="small text-muted">{{ s.duration }} min</td>
                      <td class="small text-muted">{{ s.room_no || '—' }}</td>
                      <td class="text-center fw-semibold">{{ s.max_marks }}</td>
                      <td class="text-center text-muted small">{{ s.pass_marks }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    }
  }

  <!-- ===================== SCHEDULE TAB ===================== -->
  @if (activeTab() === 'schedule') {
    <div class="exam-card mb-3"><div class="ec-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-5"><label class="form-label small fw-semibold text-muted">Select Exam</label>
          <select class="form-select" [ngModel]="selectedExamId()" (ngModelChange)="onSelectedExamChange($event)">
            @for (e of exams(); track e.id) { <option [ngValue]="e.id">{{ e.exam_name }} ({{ dataService.getClassName(e.class_name) }} Class)</option> }
          </select></div>
      </div>
    </div></div>
    @if (selectedExam()) {
      @let se = selectedExam()!;
      <div class="schedule-header-card mb-4">
        <div class="shc-type" [style.background]="getExamTypeColor(se.exam_type)">{{ se.exam_type }}</div>
        <div>
          <h5 class="shc-name">{{ se.exam_name }}</h5>
          <p class="shc-meta">{{ dataService.getClassName(se.class_name) }} Class · AY {{ se.academic_year }} · {{ formatDate(se.start_date) }} to {{ formatDate(se.end_date) }}</p>
        </div>
        <span class="{{ getExamStatusClass(se.status) }} ms-auto">{{ se.status }}</span>
      </div>
      <div class="schedule-grid">
        @for (s of se.schedule; track s.subject + s.date + $index; let i = $index) {
          <div class="schedule-card">
            <div class="sc-day-badge">
              @if (s.date) { {{ getDayNameShort(s.date) }} }
              @else { — }
            </div>
            <div class="sc-date">{{ formatDate(s.date) }}</div>
            <div class="sc-subject">{{ dataService.getSubjectName(s.subject) }}</div>
            <div class="sc-time"><i class="bi bi-clock me-1"></i>{{ formatTime(s.start_time) }} ({{ s.duration }} min)</div>
            <div class="sc-marks-row">
              <span class="sc-max">Max: {{ s.max_marks }}</span>
              <span class="sc-pass">Pass: {{ s.pass_marks }}</span>
            </div>
            @if (s.room_no) { <div class="sc-room"><i class="bi bi-geo-alt me-1"></i>{{ dataService.getRoomName(s.room_no) }}</div> }
          </div>
        }
        <div class="add-card" (click)="openNewSchedule()">
          <i class="bi bi-plus-circle-fill"></i>
          <span>Add Subject</span>
        </div>
      </div>
    }
  }

  <!-- ===================== MARKS ENTRY TAB ===================== -->
  @if (activeTab() === 'marks') {
    <div class="exam-card mb-3"><div class="ec-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-4"><label class="form-label small fw-semibold text-muted">Exam</label>
          <select class="form-select" [ngModel]="selectedExamId()" (ngModelChange)="onSelectedExamChange($event)">
            @for (e of exams(); track e.id) { <option [ngValue]="e.id">{{ e.exam_name }}</option> }
          </select></div>
        <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Class</label>
          <select class="form-select" [ngModel]="marksClass()" (ngModelChange)="marksClass.set($event)"
            disabled>
            @for (c of classOptions; track c) { <option [ngValue]="c.code">{{ c.name }}</option> }
          </select></div>
        <div class="col-md-3"><label class="form-label small fw-semibold text-muted">Search Student</label>
          <input class="form-control" [ngModel]="marksSearch()" (ngModelChange)="marksSearch.set($event)" placeholder="Name or roll no…"></div>
      </div>
      <small class="text-muted mt-2 d-block">{{ marksResults().length }} students</small>
    </div></div>

    <div class="exam-card"><div class="ec-body p-0">
      @if (marksResults().length === 0) {
        <div class="empty-state">
          <i class="bi bi-pencil"></i>
          <p>No students found for this exam and class.</p>
          <button class="btn exam-btn-primary btn-sm mt-2" (click)="openNewMarkEntry()" [disabled]="!selectedExam()">
            + Add Student Marks
          </button>
        </div>
      } @else {
        <div class="table-responsive">
          <table class="exam-table marks-list-table">
            <thead><tr>
              <th>Roll No.</th><th>Student</th>
              @for (s of marksResults()[0]?.subject_marks ?? []; track s.subject) {
                <th class="text-center">{{ s.subject }}<div class="small text-muted">/{{ s.max_marks }}</div></th>
              }
              <th class="text-center">Total</th><th class="text-center">%</th><th class="text-center">Grade</th><th class="text-center">Result</th><th class="text-center">Action</th>
            </tr></thead>
            <tbody>
              @for (r of marksResults(); track r.id) {
                <tr>
                  <td class="font-monospace small fw-semibold">{{ r.roll_no }}</td>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <div class="marks-avatar">{{ r.student_name.charAt(0) }}</div>
                      <div><div class="fw-semibold small">{{ r.student_name }}</div><div class="small text-muted">{{ r.student_id }}</div></div>
                    </div>
                  </td>
                  @for (sm of r.subject_marks; track sm.subject) {
                    <td class="text-center">
                      @if (sm.is_absent) { <span class="absent-chip">AB</span> }
                      @else if (sm.marks_obtained === null) { <span class="not-entered">—</span> }
                      @else {
                        <span class="marks-cell" [class.fail-marks]="sm.marks_obtained < sm.pass_marks">{{ sm.marks_obtained }}</span>
                      }
                    </td>
                  }
                  <td class="text-center fw-bold">{{ r.total_marks }}/{{ r.max_total }}</td>
                  <td class="text-center fw-bold" [style.color]="getGradeColor(r.grade)">{{ r.percentage }}%</td>
                  <td class="text-center">
                    <span class="grade-badge" [style.background]="getGradeColor(r.grade) + '22'" [style.color]="getGradeColor(r.grade)">{{ r.grade }}</span>
                  </td>
                  <td class="text-center"><span class="{{ getResultClass(r.result) }}">{{ r.result }}</span></td>
                  <td class="text-center">
                    <div class="d-flex gap-1 justify-content-center">
                      <button class="marks-edit-btn" (click)="openMarkEntry(r)" title="Enter Marks"><i class="bi bi-pencil-fill"></i></button>
                      <button class="marks-ht-btn" (click)="openHallTicket(r)" title="Hall Ticket"><i class="bi bi-ticket-fill"></i></button>
                      <button class="marks-ms-btn" (click)="openMarksheet(r)" title="Report Card"><i class="bi bi-file-earmark-text-fill"></i></button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div></div>
  }

  <!-- ===================== RESULTS TAB ===================== -->
  @if (activeTab() === 'results') {
    <div class="exam-card mb-3"><div class="ec-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-5"><label class="form-label small fw-semibold text-muted">Select Exam</label>
          <select class="form-select" [ngModel]="resultExamId()" (ngModelChange)="onResultExamChange($event)">
            @for (e of exams(); track e.id) { <option [ngValue]="e.id">{{ e.exam_name }} ({{ dataService.getClassName(e.class_name) }})</option> }
          </select></div>
        <div class="col-md-4"><label class="form-label small fw-semibold text-muted">Search</label>
          <input class="form-control" [ngModel]="resultSearch()" (ngModelChange)="resultSearch.set($event)" placeholder="Name or student ID…"></div>
        <div class="col-auto">
          @if (examResults().length > 0 && !examResults()[0]?.is_published) {
            <button class="btn btn-success btn-sm d-flex align-items-center gap-2" (click)="publishConfirm.set(true)">
              <i class="bi bi-megaphone-fill"></i>Publish Results
            </button>
          } @else if (examResults()[0]?.is_published) {
            <span class="published-chip"><i class="bi bi-check2-circle me-1"></i>Published</span>
          }
        </div>
      </div>
    </div></div>

    <!-- Result Summary Cards -->
    @if (examResults().length > 0) {
      <div class="result-summary-row mb-4">
        <div class="rsr-item"><div class="rsr-val text-success">{{ resultStats().pass }}</div><div class="rsr-label">Pass</div></div>
        <div class="rsr-item"><div class="rsr-val text-danger">{{ resultStats().fail }}</div><div class="rsr-label">Fail</div></div>
        <div class="rsr-item"><div class="rsr-val text-primary">{{ resultStats().passRate }}%</div><div class="rsr-label">Pass Rate</div></div>
        <div class="rsr-item"><div class="rsr-val text-info">{{ resultStats().avg }}%</div><div class="rsr-label">Class Average</div></div>
        <div class="rsr-item"><div class="rsr-val text-warning">{{ resultStats().highest }}%</div><div class="rsr-label">Highest</div></div>
      </div>

      <!-- Results Table -->
      <div class="exam-card"><div class="ec-body p-0">
        <div class="table-responsive">
          <table class="exam-table result-table">
            <thead><tr>
              <th>Rank</th><th>Roll No.</th><th>Student</th>
              @for (s of examResults()[0]?.subject_marks ?? []; track s.subject) {
                <th class="text-center d-none d-lg-table-cell">{{ s.subject }}<div class="small text-muted">/{{ s.max_marks }}</div></th>
              }
              <th class="text-center">Total</th><th class="text-center">%</th><th class="text-center">Grade</th><th class="text-center">GPA</th><th class="text-center">Result</th><th class="text-center">Actions</th>
            </tr></thead>
            <tbody>
              @for (r of examResults(); track r.id) {
                <tr [class.top-result]="r.rank <= 3" [class.fail-row]="r.result === 'Fail'">
                  <td class="text-center">
                    @if (r.rank === 1) { <span class="rank-badge gold">🥇 1</span> }
                    @else if (r.rank === 2) { <span class="rank-badge silver">🥈 2</span> }
                    @else if (r.rank === 3) { <span class="rank-badge bronze">🥉 3</span> }
                    @else { <span class="rank-plain">{{ r.rank }}</span> }
                  </td>
                  <td class="font-monospace small fw-semibold">{{ r.roll_no }}</td>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <div class="res-avatar" [style.background]="getGradeColor(r.grade)">{{ r.student_name.charAt(0) }}</div>
                      <div><div class="fw-semibold small">{{ r.student_name }}</div><div class="small text-muted">{{ r.student_id }}</div></div>
                    </div>
                  </td>
                  @for (sm of r.subject_marks; track sm.subject) {
                    <td class="text-center d-none d-lg-table-cell">
                      @if (sm.is_absent) { <span class="absent-chip">AB</span> }
                      @else { <span [class.fail-marks]="(sm.marks_obtained ?? 0) < sm.pass_marks">{{ sm.marks_obtained }}</span> }
                    </td>
                  }
                  <td class="text-center fw-bold">{{ r.total_marks }}<span class="text-muted">/{{ r.max_total }}</span></td>
                  <td class="text-center fw-bold" [style.color]="getGradeColor(r.grade)">{{ r.percentage }}%</td>
                  <td class="text-center">
                    <span class="grade-badge" [style.background]="getGradeColor(r.grade) + '22'" [style.color]="getGradeColor(r.grade)">
                      {{ r.grade }}
                    </span>
                  </td>
                  <td class="text-center fw-semibold">{{ calcGPA(r.percentage) }}</td>
                  <td class="text-center"><span class="{{ getResultClass(r.result) }}">{{ r.result }}</span></td>
                  <td class="text-center">
                    <div class="d-flex gap-1 justify-content-center">
                      <button class="ht-btn" (click)="openHallTicket(r)" title="Hall Ticket"><i class="bi bi-ticket-perforated-fill"></i></button>
                      <button class="marks-ms-btn" (click)="openMarksheet(r)" title="Report Card"><i class="bi bi-file-earmark-text-fill"></i></button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div></div>
    } @else {
      <div class="empty-state"><i class="bi bi-award"></i><p>No results found for the selected exam</p></div>
    }
  }

  <!-- ===================== REPORTS TAB ===================== -->
  @if (activeTab() === 'reports') {
    <div class="exam-card mb-3"><div class="ec-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-5"><label class="form-label small fw-semibold text-muted">Select Exam</label>
          <select class="form-select" [ngModel]="reportExamId()" (ngModelChange)="onReportExamChange($event)">
            @for (e of exams(); track e.id) { <option [ngValue]="e.id">{{ e.exam_name }} ({{ dataService.getClassName(e.class_name) }})</option> }
          </select></div>
        <div class="col-md-4"><label class="form-label small fw-semibold text-muted">Search Student</label>
          <input class="form-control" [ngModel]="reportSearch()" (ngModelChange)="reportSearch.set($event)" placeholder="Name, roll no. or ID…"></div>
        <div class="col-auto">
          <div class="report-view-toggle">
            <button class="rvt-btn" [class.active]="reportView() === 'list'" (click)="reportView.set('list')" type="button">
              <i class="bi bi-list-ul me-1"></i>List
            </button>
            <button class="rvt-btn" [class.active]="reportView() === 'charts'" (click)="reportView.set('charts')" type="button">
              <i class="bi bi-bar-chart-fill me-1"></i>Charts
            </button>
          </div>
        </div>
      </div>
    </div></div>

    @let repResults = reportResults();
    @if (repResults.length > 0) {

      @if (reportView() === 'list') {
        <div class="exam-card">
          <div class="ec-header"><i class="bi bi-table me-2"></i>Result List · {{ reportExam()?.exam_name }}</div>
          <div class="ec-body p-0">
            <div class="table-responsive">
              <table class="exam-table result-table">
                <thead>
                  <tr>
                    <th>Rank</th><th>Roll No.</th><th>Student</th>
                    <th class="text-center">Total</th><th class="text-center">%</th>
                    <th class="text-center">Grade</th><th class="text-center">GPA</th>
                    <th class="text-center">Result</th><th class="text-center">Report Card</th>
                  </tr>
                </thead>
                <tbody>
                  @for (r of repResults; track r.id) {
                    <tr [class.top-result]="r.rank <= 3" [class.fail-row]="r.result === 'Fail'">
                      <td class="text-center">
                        @if (r.rank === 1) { <span class="rank-badge gold">🥇 1</span> }
                        @else if (r.rank === 2) { <span class="rank-badge silver">🥈 2</span> }
                        @else if (r.rank === 3) { <span class="rank-badge bronze">🥉 3</span> }
                        @else { <span class="rank-plain">{{ r.rank }}</span> }
                      </td>
                      <td class="font-monospace small fw-semibold">{{ r.roll_no }}</td>
                      <td>
                        <div class="d-flex align-items-center gap-2">
                          <div class="res-avatar" [style.background]="getGradeColor(r.grade)">{{ r.student_name.charAt(0) }}</div>
                          <div>
                            <div class="fw-semibold small">{{ r.student_name }}</div>
                            <div class="small text-muted">{{ r.student_id }} · {{ r.class_name }}</div>
                          </div>
                        </div>
                      </td>
                      <td class="text-center fw-bold">{{ r.total_marks }}<span class="text-muted">/{{ r.max_total }}</span></td>
                      <td class="text-center fw-bold" [style.color]="getGradeColor(r.grade)">{{ r.percentage }}%</td>
                      <td class="text-center">
                        <span class="grade-badge" [style.background]="getGradeColor(r.grade)+'22'" [style.color]="getGradeColor(r.grade)">{{ r.grade }}</span>
                      </td>
                      <td class="text-center fw-semibold">{{ calcGPA(r.percentage) }}</td>
                      <td class="text-center"><span class="{{ getResultClass(r.result) }}">{{ r.result }}</span></td>
                      <td class="text-center">
                        <button class="btn btn-outline-primary btn-sm" (click)="openMarksheet(r)">
                          <i class="bi bi-file-earmark-text me-1"></i>View
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      }

      @if (reportView() === 'charts') {
        <div class="row g-4">

          <!-- Performance Overview -->
          <div class="col-lg-4">
            <div class="exam-card h-100">
              <div class="ec-header"><i class="bi bi-pie-chart-fill me-2"></i>Performance Overview</div>
              <div class="ec-body">
                <div class="donut-wrap">
                  <svg viewBox="0 0 100 100" class="donut-svg">
                    @let circ = 251.3;
                    @let passArc = getPassArc(repResults);
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#f3f4f6" stroke-width="14"/>
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#22c55e" stroke-width="14"
                      [attr.stroke-dasharray]="passArc + ' ' + circ" stroke-dashoffset="0" stroke-linecap="round" transform="rotate(-90 50 50)"/>
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#ef4444" stroke-width="14"
                      [attr.stroke-dasharray]="(circ - passArc) + ' ' + circ" [attr.stroke-dashoffset]="'-' + passArc" stroke-linecap="round" transform="rotate(-90 50 50)"/>
                    <text x="50" y="46" text-anchor="middle" font-size="14" font-weight="800" fill="#111827">{{ getPassRate(repResults) }}%</text>
                    <text x="50" y="60" text-anchor="middle" font-size="7" fill="#6b7280">Pass Rate</text>
                  </svg>
                </div>
                <div class="donut-legend">
                  <div class="dl-item"><span class="dl-dot" style="background:#22c55e"></span>Pass: {{ getPassCount(repResults) }}</div>
                  <div class="dl-item"><span class="dl-dot" style="background:#ef4444"></span>Fail: {{ repResults.length - getPassCount(repResults) }}</div>
                </div>
                <div class="rep-key-stats mt-3">
                  <div class="rks-row"><span>Class Average</span><span class="fw-bold text-primary">{{ getClassAvg(repResults) }}%</span></div>
                  <div class="rks-row"><span>Highest Score</span><span class="fw-bold text-success">{{ getHighest(repResults) }}%</span></div>
                  <div class="rks-row"><span>Lowest Score</span><span class="fw-bold text-danger">{{ getLowest(repResults) }}%</span></div>
                  <div class="rks-row"><span>Total Students</span><span class="fw-bold">{{ repResults.length }}</span></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Grade Distribution -->
          <div class="col-lg-4">
            <div class="exam-card h-100">
              <div class="ec-header"><i class="bi bi-bar-chart-fill me-2"></i>Grade Distribution</div>
              <div class="ec-body">
                <div class="grade-dist-chart">
                  @for (g of gradeRules; track g.grade) {
                    @let gCount = getGradeCount(repResults, g.grade);
                    @let maxC   = getMaxGradeCount(repResults);
                    <div class="gdc-row">
                      <span class="gdc-grade" [style.color]="g.color" [style.background]="g.color + '18'">{{ g.grade }}</span>
                      <div class="gdc-bar-bg">
                        <div class="gdc-bar-fill" [style.width.%]="maxC > 0 ? (gCount/maxC)*100 : 0" [style.background]="g.color"></div>
                      </div>
                      <span class="gdc-count" [style.color]="g.color">{{ gCount }}</span>
                      <span class="gdc-label">{{ g.label }}</span>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- Subject-wise Analysis -->
          <div class="col-lg-4">
            <div class="exam-card h-100">
              <div class="ec-header"><i class="bi bi-list-check me-2"></i>Subject-wise Average</div>
              <div class="ec-body">
                @let repExam = reportExam();
                @for (subj of repExam?.schedule ?? []; track subj.subject) {
                  @let subPct = getSubjectAvg(repResults, subj.subject, subj.max_marks);
                  @let subAvgRaw = getSubjectAvgRaw(repResults, subj.subject, subj.max_marks);
                  <div class="subj-analysis-row">
                    <div class="sar-name">{{ subj.subject }}</div>
                    <div class="sar-bar-wrap">
                      <div class="sar-bar-bg">
                        <div class="sar-bar-fill" [style.width.%]="subPct" [style.background]="getGradeColor(calcGrade(subPct))"></div>
                      </div>
                      <span class="sar-pct" [style.color]="getGradeColor(calcGrade(subPct))">{{ subAvgRaw }}/{{ subj.max_marks }}</span>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>

          <!-- Topper List -->
          <div class="col-12">
            <div class="exam-card">
              <div class="ec-header"><i class="bi bi-trophy-fill me-2"></i>Top Performers</div>
              <div class="ec-body">
                <div class="toppers-row">
                  @for (r of getToppers(repResults); track r.id; let i = $index) {
                    <div class="topper-card" [class.tc-gold]="i===0" [class.tc-silver]="i===1" [class.tc-bronze]="i===2" (click)="openMarksheet(r)" style="cursor:pointer">
                      <div class="tc-rank">{{ i===0 ? '🥇' : i===1 ? '🥈' : i===2 ? '🥉' : '#'+(i+1) }}</div>
                      <div class="tc-avatar" [style.background]="getGradeColor(r.grade)">{{ r.student_name.charAt(0) }}</div>
                      <div class="tc-name">{{ r.student_name }}</div>
                      <div class="tc-roll">{{ r.roll_no }}</div>
                      <div class="tc-score" [style.color]="getGradeColor(r.grade)">{{ r.percentage }}%</div>
                      <span class="grade-badge" [style.background]="getGradeColor(r.grade)+'22'" [style.color]="getGradeColor(r.grade)">{{ r.grade }}</span>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

        </div>
      }
    } @else {
      <div class="empty-state"><i class="bi bi-bar-chart-line"></i><p>No results available for the selected exam</p></div>
    }
  }

</div>
